import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { StoredDeviceCreationFormParameters } from '@models/activity.model';
import { UserAsset, UserDeviceAsset } from '@models/dashboard.model';
import { calculateItemsToDelete, removeDeviceToActivateParams } from '@models/functions.model';
import { ShoppingCartData } from '@models/shopping-cart.model';
import {
  DeviceCreationFormDeviceCharacteristics,
  DeviceCreationFormFiltersConfig,
  DeviceData,
  DeviceIdentifier,
  DeviceItemDevice,
  FieldErrorMessage,
  ShoppingCategoryData,
  ShoppingCategoryItem,
  ShoppingFilterData,
  ShoppingFilterItem,
  ValidateFields
} from '@models/shopping.model';
import { FilterToken, ShoppingDataInitialLoad } from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { ModalService } from '@services/modal/modal.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { debounce } from 'lodash';
import { Observable, Subscription, asapScheduler, forkJoin, map, switchMap } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

@Component({
  selector: 'ts-device-creation-form',
  templateUrl: './device-creation-form.component.html',
  imports: [ReactiveFormsModule, TranslationPipe, DataLoaderDirective]
})
export class DeviceCreationFormComponent implements OnInit, OnDestroy {
  @Input() existing_asset: UserAsset | undefined;
  @Input() device_identifier_search_value: string | undefined;
  @Input() shopping_cart: ShoppingCartData;
  @Input() admin_override: FormControl = new FormControl(null);
  @Output() cancel_override_toggle: EventEmitter<boolean> = new EventEmitter();
  device_form: FormGroup = new FormGroup({
    category: new FormControl('', [Validators.required]),
    brand: new FormControl('', [Validators.required]),
    model: new FormControl({ value: '', disabled: true }, [Validators.required]),
    color: new FormControl({ value: '', disabled: true }, [Validators.required]),
    storage: new FormControl({ value: '', disabled: true }, [Validators.required]),
    imei: new FormControl('', [Validators.required]),
    serial_number: new FormControl('', [Validators.required])
  });
  filters_config: DeviceCreationFormFiltersConfig = {} as DeviceCreationFormFiltersConfig;
  device_characteristics: DeviceCreationFormDeviceCharacteristics = {
    category: [],
    brand: [],
    model: [],
    color: [],
    storage: []
  };
  is_loading: boolean = false;
  is_submitted: boolean = false;
  has_cleared_cart: boolean = false;
  private debounced_is_loading = debounce(() => (this.is_loading = true), 1000);
  private subscriptions: Subscription = new Subscription();
  private fetch_subscription: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private modal_svc: ModalService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    if (!this.getStoredFormParameters()) this.fetchDeviceCharacteristics();

    Object.keys(this.device_characteristics).forEach(
      (current_key: string, index: number, keys: string[]) => {
        this.subscriptions.add(
          this.device_form.controls[current_key].valueChanges.subscribe((value) => {
            if (this.shopping_cart.shopping_cart_items.length > 0 && !this.has_cleared_cart) {
              this.confirmChangesModal(
                () => this.formValueChangeLogic(current_key, index, keys, value),
                current_key
              );
            } else {
              this.formValueChangeLogic(current_key, index, keys, value);
            }
          })
        );
      }
    );

    this.subscriptions.add(
      this.admin_override.valueChanges.subscribe(() => {
        const resetAndFetch = () => {
          this.device_form.reset({
            category: '',
            brand: '',
            model: { value: '', disabled: true },
            color: { value: '', disabled: true },
            storage: { value: '', disabled: true },
            imei: '',
            serial_number: ''
          });
          this.fetchDeviceCharacteristics([], [], true);
        };

        if (this.shopping_cart.shopping_cart_items.length > 0 && !this.has_cleared_cart)
          this.confirmChangesModal(resetAndFetch, undefined, true);
        else resetAndFetch();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isFieldValid(field: string): boolean {
    const control = this.device_form.get(field) as FormControl;
    return control.invalid && this.is_submitted;
  }

  createDevice(): void {
    const order_type_id: number = this.activity_manager_svc
      .state()
      .parameter('type')?.order_type_id!;
    const param: DeviceIdentifier = {
      serial_number: this.device_form.get('serial_number')?.value,
      imei: this.device_form.get('imei')?.value
    };

    this.is_submitted = true;
    this.shopping_svc.deviceIdentifierValidate(param).subscribe((res: ValidateFields) => {
      if (!res.valid) {
        res.error_messages?.forEach((err: FieldErrorMessage) =>
          this.device_form.get(err.field_id)?.setErrors({ error: err.error_message[0] })
        );

        return;
      } else this.continueCreateDevice(order_type_id);
    });
  }

  private formValueChangeLogic(
    current_key: string,
    index: number,
    keys: string[],
    value: any
  ): void {
    for (let i = index + 1; i < keys.length; i++) {
      if (this.device_form.controls[keys[i]].value)
        this.device_form.controls[keys[i]].setValue('', { emitEvent: false });
    }

    const filter_tokens: FilterToken[] = [];
    const categories: Array<{ id: number; value: string }> = [];
    if (this.filters_config?.category) {
      if (current_key === 'category')
        categories.push({ id: this.filters_config.category, value: value });
      else if (this.device_form.value.category)
        categories.push({
          id: this.filters_config.category,
          value: this.device_form.value.category
        });
    }

    keys.forEach((key: string) => {
      const id: number = this.filters_config?.[key as keyof DeviceCreationFormFiltersConfig];
      const input_value: any = key !== current_key ? this.device_form.value[key] : value;
      if (key === 'category' || !id || !input_value) return;
      filter_tokens.push({ id, value: String(input_value) });
    });

    this.fetchDeviceCharacteristics(categories, filter_tokens);
  }

  private confirmChangesModal(callback: () => void, key?: string, cancel_override?: boolean): void {
    let modal_strings: string[] = this.translation_svc.getTranslatedModalLabels();

    this.modal_svc.confirmationDialog(
      {
        title: modal_strings[0],
        message: modal_strings[1],
        confirm_button_text: this.translation_svc.translate('continue'),
        cancel_button_text: this.translation_svc.translate('cancel'),
        onConfirm: () => {
          this.has_cleared_cart = true;
          this.shopping_svc
            .clearSpecifiedCartItems(
              calculateItemsToDelete(this.shopping_cart.shopping_cart_items),
              false
            )
            .subscribe({
              next: (res: ShoppingCartData) => {
                this.shopping_cart = removeDeviceToActivateParams(res);
                callback();
                this.modal_svc.dismissAll();
              },
              error: (err: HttpErrorResponse) => {}
            });
        },
        onCancel: () => {
          if (key) this.cancelConfirmModalLogic(key);
          if (cancel_override) this.cancel_override_toggle.emit(true);
          this.modal_svc.dismissAll();
        },
        onDismiss: () => {
          if (key) this.cancelConfirmModalLogic(key);
          if (cancel_override) this.cancel_override_toggle.emit(true);
          this.modal_svc.dismissAll();
        }
      },
      {
        size: 'dialog-centered',
        centered: true,
        backdrop: 'static'
      }
    );
  }

  private cancelConfirmModalLogic(key: string): void {
    switch (key) {
      case 'category':
        this.device_form.controls[key].setValue(this.existing_asset?.device.type);
        break;
      case 'brand':
        this.device_form.controls[key].setValue(
          this.existing_asset?.asset_selection_case === 3
            ? this.existing_asset?.device.manufacturer
            : ''
        );
        break;
      case 'model':
        this.device_form.controls[key].setValue(this.existing_asset?.device.model);
        break;
      case 'color':
        this.device_form.controls[key].setValue(this.existing_asset?.device.color);
        break;
      case 'storage':
        this.device_form.controls[key].setValue(this.existing_asset?.device.size);
        break;
      default:
        break;
    }
  }

  private continueCreateDevice(order_type_id: number): void {
    let initial_load: ShoppingDataInitialLoad = {
      categories: [],
      filters: [],
      orderTypeId: order_type_id,
      deviceId: null,
      include_admin_options:
        this.admin_override.value !== null ? this.admin_override.value : undefined
    };

    this.shopping_svc.getDevices(initial_load).subscribe((res: DeviceData) => {
      let filters: ShoppingFilterData[] = res.filters;
      filters.forEach((filter: ShoppingFilterData) => {
        let shopping_filter_item: ShoppingFilterItem;
        switch (filter.config.filter_name) {
          case 'manufacturer':
            shopping_filter_item = {
              id: filter.config.filter_config_id,
              value: this.device_form.controls['brand'].value,
              filter_name: filter.config.filter_name
            };
            initial_load.filters.push(shopping_filter_item);
            break;
          case 'model':
            shopping_filter_item = {
              id: filter.config.filter_config_id,
              value: this.device_form.controls['model'].value,
              filter_name: filter.config.filter_name
            };
            initial_load.filters.push(shopping_filter_item);
            break;
          case 'color':
            shopping_filter_item = {
              id: filter.config.filter_config_id,
              value: this.device_form.controls['color'].value,
              filter_name: filter.config.filter_name
            };
            initial_load.filters.push(shopping_filter_item);
            break;
          case 'size':
            shopping_filter_item = {
              id: filter.config.filter_config_id,
              value: this.device_form.controls['storage'].value,
              filter_name: filter.config.filter_name
            };
            initial_load.filters.push(shopping_filter_item);
            break;
          default:
            break;
        }
      });

      let categories: ShoppingCategoryData[] = res.category;
      categories.forEach((category: ShoppingCategoryData) => {
        if (category.config.filter_name === 'product_category') {
          initial_load.categories = [
            {
              id: category.config.filter_config_id,
              value: this.device_form.controls['category'].value
            }
          ];
        }
      });

      this.shopping_svc.getDevices(initial_load).subscribe((res1: DeviceData) => {
        res1.items[0].devices = res1.items[0].devices.filter((device: DeviceItemDevice) => {
          return (
            device.manufacturer === this.device_form.controls['brand'].value &&
            device.model === this.device_form.controls['model'].value &&
            device.color === this.device_form.controls['color'].value &&
            device.size === this.device_form.controls['storage'].value
          );
        });

        let asset: UserDeviceAsset = {
          device: {
            id: res1.items[0].devices[0].id,
            manufacturer: this.device_form.controls['brand'].value,
            model: this.device_form.controls['model'].value,
            color: this.device_form.controls['color'].value,
            type: this.device_form.controls['category'].value,
            imei: this.device_form.controls['imei'].value,
            device_catalog_id: res1.items[0].devices[0].device_catalog_id,
            size: this.device_form.controls['storage'].value,
            serial_number: this.device_form.controls['serial_number'].value,
            tech_specs: {
              sim_compatibility: res1.items[0].devices[0].device_compatibility
            },
            image_url: res1.items[0].devices[0].image_url,
            normalized_color: res1.items[0].devices[0].color,
            multisim_device: false
          }
        };

        const cart_payload: ShoppingCartData = {
          ...this.shopping_cart,
          country_id:
            asset.line?.country_id ?? this.activity_manager_svc.state().parameter('country')?.id,
          orderTypeId: order_type_id,
          shopping_cart_items: [],
          progress_stepper: this.activity_manager_svc
            .state()
            .currentStep(this.activity_manager_svc.state().activeStepIndex()),
          device_include_admin_options: this.admin_override.value,
          existing_asset: {
            ...asset,
            asset_from_inventory: false,
            asset_selection_case: 3,
            device_identifier_search_value: this.device_identifier_search_value
          } as UserAsset
        };

        this.shopping_svc.sendShoppingItems(cart_payload).subscribe({
          next: (res2: ShoppingCartData) => {
            this.activity_manager_svc
              .state()
              .setParameter('device_include_admin_options', this.admin_override.value);
            this.storeFormParameters(res2);
            this.activity_manager_svc.navigateToNextStep({
              queryParams: {
                carrierId: res1.items[0].devices[0].carrier_id,
                deviceId: res1.items[0].devices[0].id,
                categories: JSON.stringify(initial_load.categories),
                filters: JSON.stringify(initial_load.filters)
              }
            });
          }
        });
      });
    });
  }

  private fetchDeviceCharacteristics(
    categories: ShoppingDataInitialLoad['categories'] = [],
    filter_tokens: FilterToken[] = [],
    skip_asset_population: boolean = false
  ): void {
    const asset: UserAsset | undefined =
      !skip_asset_population &&
      !categories.length &&
      this.existing_asset &&
      this.existing_asset.asset_selection_case === 3
        ? this.existing_asset
        : undefined;
    let observable: Observable<DeviceData> = this.shopping_svc.getDevicesForTheirFilters(
      categories,
      filter_tokens,
      this.admin_override.value
    );
    // need to do each request with incremental filters in order not to have the dropdowns reduced
    // to the selected value as it is if you do just one request with all filters set with existing asset values
    // Might want to discuss with the BE team to see if there is not a better way to do this
    if (asset)
      observable = observable.pipe(
        switchMap((devices: DeviceData) => {
          this.handleCategoriesResult(devices.category, false);
          this.handleFiltersResult(devices.filters, false);
          categories = [];
          const filter_tokens_category: FilterToken[] = [];
          const filter_tokens_brand: FilterToken[] = [];
          const filter_tokens_model: FilterToken[] = [];
          const filter_tokens_color: FilterToken[] = [];
          Object.entries(this.filters_config).forEach(([key, id]: [string, number]) => {
            switch (key) {
              case 'category':
                if (!categories.length) categories.push({ id, value: asset.device.type });
                break;
              case 'brand':
                if (asset.device.manufacturer)
                  filter_tokens_brand.push({ id, value: asset.device.manufacturer });
                break;
              case 'model':
                if (asset.device.manufacturer)
                  filter_tokens_model.push({ id, value: asset.device.manufacturer });
                if (asset.device.model) filter_tokens_model.push({ id, value: asset.device.model });
                break;
              case 'color':
                if (asset.device.manufacturer)
                  filter_tokens_color.push({ id, value: asset.device.manufacturer });
                if (asset.device.model) filter_tokens_color.push({ id, value: asset.device.model });
                if (asset.device.color) filter_tokens_color.push({ id, value: asset.device.color });
                break;
            }
          });
          return forkJoin([
            this.shopping_svc.getDevicesForTheirFilters(categories, filter_tokens_category),
            this.shopping_svc.getDevicesForTheirFilters(categories, filter_tokens_brand),
            this.shopping_svc.getDevicesForTheirFilters(categories, filter_tokens_model),
            this.shopping_svc.getDevicesForTheirFilters(categories, filter_tokens_color)
          ]).pipe(
            map(
              ([devices_category, devices_brand, devices_model, devices_colors]: [
                DeviceData,
                DeviceData,
                DeviceData,
                DeviceData
              ]) => {
                this.handleCategoriesResult(devices_category.category);
                this.handleFiltersResult(devices_category.filters);
                return {
                  ...devices_category,
                  filters: [
                    devices_category.filters.find(
                      (filter: ShoppingFilterData) => filter.config.filter_name === 'manufacturer'
                    ),
                    devices_brand.filters.find(
                      (filter: ShoppingFilterData) => filter.config.filter_name === 'model'
                    ),
                    devices_model.filters.find(
                      (filter: ShoppingFilterData) => filter.config.filter_name === 'color'
                    ),
                    devices_colors.filters.find(
                      (filter: ShoppingFilterData) => filter.config.filter_name === 'size'
                    )
                  ].filter((filter: ShoppingFilterData | undefined) =>
                    Boolean(filter)
                  ) as ShoppingFilterData[]
                };
              }
            )
          );
        })
      );
    this.debounced_is_loading();
    this.fetch_subscription.unsubscribe();
    this.fetch_subscription = observable.subscribe((device_data: DeviceData) => {
      this.handleCategoriesResult(device_data.category);
      this.handleFiltersResult(device_data.filters);
      this.debounced_is_loading.cancel();
      this.is_loading = false;
      if (asset) asapScheduler.schedule(() => this.populateFormFromAsset(asset!));
    });
    this.subscriptions.add(this.fetch_subscription);
  }

  private handleCategoriesResult(
    categories: DeviceData['category'],
    populate: boolean = true
  ): void {
    if (!categories || !categories.length || !categories[0].items.length) return;

    this.filters_config.category = categories[0].config.filter_config_id;

    if (!populate) return;

    const category_item: ShoppingCategoryItem = categories[0].items.filter(
      (item: ShoppingCategoryItem) => item.active
    )[0];

    this.device_characteristics.category = categories[0].items.map((category) => ({
      id: category.item,
      name: category.display_name
    }));

    setTimeout(() => {
      this.device_form.controls['category'].setValue(category_item.item, { emitEvent: false });
    });
  }

  private handleFiltersResult(filters: DeviceData['filters'], populate: boolean = true): void {
    filters.forEach((filter: ShoppingFilterData) => {
      let key: keyof DeviceCreationFormFiltersConfig | '' = '';
      let value_to_check: keyof DeviceCreationFormFiltersConfig | '' = '';

      switch (filter.config.filter_name) {
        case 'manufacturer':
          key = 'brand';
          break;

        case 'model':
          key = 'model';
          value_to_check = 'brand';
          break;

        case 'color':
          key = 'color';
          value_to_check = 'model';
          break;

        case 'size':
          key = 'storage';
          value_to_check = 'color';
          break;

        default:
          break;
      }

      if (!key) return;

      this.filters_config[key] = filter.config.filter_config_id;

      if (!populate) return;

      let selected_filter: string = '';
      const characteristics: ShoppingFilterItem[] = filter.items.map(
        (filter: ShoppingFilterItem) => {
          if (filter.active) selected_filter = filter.item ?? '';
          return { id: filter.item, name: filter.item };
        }
      );

      this.device_form.controls[key].setValue(selected_filter, {
        emitEvent: false
      });
      if (!selected_filter || !this.device_characteristics[key].length)
        this.device_characteristics[key] = characteristics as { id: string; name: string }[];

      if (!value_to_check) return;

      if (!this.device_form.controls[value_to_check].value) {
        this.device_form.controls[key].setValue('', { emitEvent: false });
        this.device_form.controls[key].disable({ emitEvent: false });
      } else this.device_form.controls[key].enable({ emitEvent: false });
    });
  }

  private storeFormParameters(cart_data: ShoppingCartData): void {
    if (!cart_data.existing_asset?.device) return;

    this.activity_manager_svc.state().setParameter('device_form_parameters', {
      thumbprint:
        `${cart_data.existing_asset.device.type}_${cart_data.existing_asset.device.manufacturer}_` +
        `${cart_data.existing_asset.device.model}_${cart_data.existing_asset.device.color}_${cart_data.existing_asset.device.size}`,
      config: this.filters_config,
      characteristics: this.device_characteristics
    });
    this.activity_manager_svc.state().saveConfig();
  }

  private getStoredFormParameters(): boolean {
    if (this.existing_asset?.asset_selection_case !== 3) return false;

    const stored_parameters: StoredDeviceCreationFormParameters | undefined =
      this.activity_manager_svc.state().parameter('device_form_parameters');

    if (!stored_parameters) return false;

    const thumbprint: string =
      `${this.existing_asset.device.type}_${this.existing_asset.device.manufacturer}_` +
      `${this.existing_asset.device.model}_${this.existing_asset.device.color}_${this.existing_asset.device.size}`;

    if (thumbprint !== stored_parameters.thumbprint) return false;

    this.filters_config = stored_parameters.config;
    this.device_characteristics = stored_parameters.characteristics;

    this.device_form.controls['category'].setValue(this.existing_asset.device.type, {
      emitEvent: false
    });
    this.populateFormFromAsset(this.existing_asset);

    return true;
  }

  private populateFormFromAsset(asset: UserAsset): void {
    if (asset.device.manufacturer) {
      this.device_form.controls['brand'].setValue(asset.device.manufacturer, {
        emitEvent: false
      });
      this.device_form.controls['brand'].enable({ emitEvent: false });
    }
    if (asset.device.model) {
      this.device_form.controls['model'].setValue(asset.device.model, {
        emitEvent: false
      });
      this.device_form.controls['model'].enable({ emitEvent: false });
    }
    if (asset.device.color) {
      this.device_form.controls['color'].setValue(asset.device.color, {
        emitEvent: false
      });
      this.device_form.controls['color'].enable({ emitEvent: false });
    }
    if (asset.device.size) {
      this.device_form.controls['storage'].setValue(asset.device.size, {
        emitEvent: false
      });
      this.device_form.controls['storage'].enable({ emitEvent: false });
    }
    this.device_form.controls['imei'].setValue(asset.device.imei, { emitEvent: false });
    this.device_form.controls['serial_number'].setValue(asset.device.serial_number, {
      emitEvent: false
    });
  }
}
